import { AccountStore } from '@/constants/Account';
import { compressJsonData, delay, equals, getLocalStorageItem, getLocalStorageItemObject, getLocalStoragePrefix, isDateLongerThanMinutes, log, purifyJson, removeLocalStorageItem, setLocalStorageItem, setLocalStorageItemObject, sortKeysByLastUpdateDate, tryParseJson, waitForCondition } from "@/util/algorithm";

import { message } from 'antd';
import { openDB } from 'idb';
import { getLbl, getUserLocale } from '@/lngProvider';
import { clientConfigInitializedSignal, parseServerInitializedSignal, windowIdentitySignal } from '@/util/signal';
import system from '@/parse-api/system';
import { keycloakApi } from '@/parse-api/keycloak-sso';
import { DataRetrievalLock } from '@/util/singleton';
import { registerService } from '@/util/centralizedService';

const localLastUpdateDateMap = 'localLastUpdateDateMap';

const CACHED_KEYS = [
  'getSystemVersion',
  'registerDataClassConfig',
  'dataExplorer_getDefaultDataClasses',
  'dataExplorer_getAllDataClasses',
  'dataExplorer_getActiveByKey{"dataClass":"SystemForm"',
  'dataExplorer_getActiveByKey{"dataClass":"SystemFlow"',
  'dataExplorer_getActiveByKey{"dataClass":"SystemPage"',
  'dataExplorer_getDataByKey{"dataClass":"SystemSelect"',
  'dataExplorer_getDataByKey{"dataClass":"SystemParameter"',
  'dataExplorer_getDataByKey{"dataClass":"User"',
  'getSystemPageActionPolicies',
  'getSystemFormActionPolicies',
  'dataExplorer_getAllSearch',
  'dataExplorer_getAllSearchResult',
  'getDataPolicyData',
]

const SELECT_KEYS = [
  'prepareSystemSelectOption',
  localLastUpdateDateMap,
]

const RELOAD_PAGE_KEYS = [
  'registerDataClassConfig',
  'dataExplorer_getActiveByKey{"dataClass":"SystemForm"',
  'dataExplorer_getActiveByKey{"dataClass":"SystemFlow"',
  'dataExplorer_getActiveByKey{"dataClass":"SystemPage"',
]

const ACTIVE_DATA_KEYS = [
  'registerDataClassConfig',
  'dataExplorer_getDefaultDataClasses',
  'dataExplorer_getAllDataClasses',
  'dataExplorer_getActiveByKey{"dataClass":"SystemForm"',
  'dataExplorer_getActiveByKey{"dataClass":"SystemFlow"',
  'dataExplorer_getActiveByKey{"dataClass":"SystemPage"',
  'dataExplorer_getDataByKey{"dataClass":"SystemSelect"',
  'dataExplorer_getDataByKey{"dataClass":"SystemParameter"',
  'dataExplorer_getDataByKey{"dataClass":"User"',
  'getSystemPageActionPolicies',
  'getSystemFormActionPolicies',
]

const SHARED_DATA_KEYS = [
  'dataExplorer_getAllSearch',
  'dataExplorer_getAllSearchResult',
]

const RELOAD_DATA_KEYS = [
  'getDataPolicyData',
]

const isMatchedCacheKey = (key, keys) => {
  let matched = false;
  for (const element of keys) {
    if (key.startsWith(element)) {
      matched = true;
      break;
    }
  }
  return matched;
}

const isActiveDataKey = (key) => {
  return isMatchedCacheKey(key, ACTIVE_DATA_KEYS);
}

const isSharedDataKey = (key) => {
  return isMatchedCacheKey(key, SHARED_DATA_KEYS);
}

const isReloadDataKey = (key) => {
  return isMatchedCacheKey(key, RELOAD_DATA_KEYS);
}

const isReloadPageKey = (key) => {
  return isMatchedCacheKey(key, RELOAD_PAGE_KEYS);
}

const isSelectKey = (key) => {
  return isMatchedCacheKey(key, SELECT_KEYS);
}

const getKeyStore = (key) => {
  if (key.includes('"isPreview":true')) return null;
  if (key.includes('"action":"save"')) return null;
  if (key.includes('"action":"delete"')) return null;

  let store = null;
  if (isMatchedCacheKey(key, CACHED_KEYS)) {
    store = "cache";
  }
  if (isMatchedCacheKey(key, SELECT_KEYS)) {
    store = "select";
  }
  return store;
}

const dbPromise = openDB('cache-store', 1, {
  upgrade(db) {
    db.createObjectStore('cache');
    db.createObjectStore('select');
  },
});

const MemoryStore = new Map();

function mdb_store(store) {
  let cache = MemoryStore.get(store);
  if (!cache) {
    cache = new Map();
    MemoryStore.set(store, cache);
  }
  return cache;
}

function mdb_get(store, key) {
  if (store) {
    const cache = mdb_store(store);
    return cache.get(key);
  } else {
    return null;
  }
}

function mdb_set(store, key, val) {
  if (store) {
    const cache = mdb_store(store);
    cache.set(key, val);
  }
}

function mdb_del(store, key) {
  if (store) {
    const cache = mdb_store(store);
    cache.delete(key);
  }
}


function mdb_clear(store) {
  const cache = mdb_store(store);
  return cache.clear();
}

export async function idb_get(key) {
  try {
    const store = getKeyStore(key);
    if (store) {
      const val = mdb_get(store, key);
      if (val) {
        return val;
      } else {
        return (await dbPromise).get(store, key);
      }
    } else {
      return null;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
}

export async function idb_set(key, val) {
  try {
    const store = getKeyStore(key);
    if (store) {
      mdb_set(store, key, val);
      return (await dbPromise).put(store, val, key);
    } else {
      return null;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
}

export async function idb_partialUpdate(key, record, dataIndex) {
  try {
    const val = await idb_get(key);
    if (val && Array.isArray(val)) {
      const index = val.findIndex((v => v[dataIndex] === record[dataIndex]));
      if (index !== -1) {
        val[index] = record;
        await idb_set(key, [...val]);
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
}

export async function idb_del(key) {
  try {
    const store = getKeyStore(key);
    if (store) {
      mdb_del(store, key);

      return (await dbPromise).delete(store, key);
    } else {
      return null;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
}

export async function idb_clear(store) {
  mdb_clear(store);
  return (await dbPromise).clear(store);
}

export async function idb_allKeys(store) {
  try {
    if (store) {
      return (await dbPromise).getAllKeys(store);
    } else {
      return null;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
}

// ============================================================
// HTTP API Client - replaces Parse SDK
// ============================================================

/**
 * Get the server URL from AccountStore.
 * Falls back to the current origin if not configured.
 * Strips a trailing '/parse' if present, since all endpoint paths
 * already include the '/parse/' prefix.
 */
export const getServerUrl = () => {
  let url = AccountStore.PARSE_HOST_URL || window.location.origin;
  // Remove trailing slash
  url = url.replace(/\/+$/, '');
  // Remove trailing '/parse' if present (PARSE_HOST_URL may include it)
  url = url.replace(/\/parse$/, '');
  return url;
};

/**
 * Get the session token from cookie or localStorage.
 */
const getSessionToken = () => {
  // Try cookie first
  const match = document.cookie.match(/session_token=([^;]+)/);
  if (match) return match[1];
  // Try localStorage
  const userObj = getLocalStorageItemObject('user_obj');
  if (userObj && userObj.sessionToken) return userObj.sessionToken;
  return null;
};

/**
 * Low-level HTTP call to a Parse-compatible cloud function endpoint.
 * Replaces Parse.Cloud.run()
 */
const callCloudFunction = async (functionName, params) => {
  const serverUrl = getServerUrl();
  const url = `${serverUrl}/parse/functions/${functionName}`;
  const sessionToken = getSessionToken();

  const headers = {
    'Content-Type': 'text/plain',
  };
  if (sessionToken) {
    headers['X-Parse-Session-Token'] = sessionToken;
  }

  let response;
  try {
    response = await window.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
  } catch (networkErr) {
    // Network error (server unreachable, CORS, etc.)
    updateServerOfflineStatus(networkErr);
    throw networkErr;
  }

  if (!response.ok) {
    let errorMsg = `HTTP ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMsg = errorBody.error || errorBody.message || errorMsg;
    } catch (e) {
      // response body not JSON
      try {
        errorMsg = await response.text();
      } catch (e2) {}
    }
    const error = new Error(errorMsg);
    error.code = response.status;
    throw error;
  }

  const data = await response.json();
  // The backend wraps results in { result: ... } (Parse-compatible format)
  if (data && typeof data === 'object' && 'result' in data) {
    return data.result;
  }
  return data;
};

/**
 * Initialize the API client (replaces Parse.initialize).
 * No longer needs Parse SDK initialization, just marks the signal as ready.
 */
export const initializeParseSDK = async () => {
  console.log('initializeParseSDK()...')
  await clientConfigInitializedSignal.waitFor('value', true);
  // No Parse SDK to initialize - just mark as ready
  parseServerInitializedSignal.value = true;
}

// Export a stub `parse` object for backward compatibility
// (some files import `parse` from config)
export const parse = {
  Cloud: {
    run: (fn, params) => callCloudFunction(fn, params),
  },
  User: {
    current: async () => {
      const token = getSessionToken();
      if (!token) return null;
      try {
        const serverUrl = getServerUrl();
        const response = await window.fetch(`${serverUrl}/parse/users/me`, {
          method: 'GET',
          headers: {
            'X-Parse-Session-Token': token,
          },
          // credentials not needed - session token sent via header
        });
        if (!response.ok) return null;
        const user = await response.json();
        return {
          ...user,
          toJSON: () => user,
          get: (key) => user[key],
          id: user.objectId,
        };
      } catch (e) {
        return null;
      }
    },
    logOut: async () => {
      try {
        const serverUrl = getServerUrl();
        const token = getSessionToken();
        const headers = {};
        if (token) headers['X-Parse-Session-Token'] = token;
        await window.fetch(`${serverUrl}/parse/logout`, {
          method: 'POST',
          headers,
          // credentials not needed - session token sent via header
        });
      } catch (e) {}
      removeLocalStorageItem('user_obj');
    },
    become: async (sessionToken) => {
      // Login with session token via /parse/login
      const serverUrl = getServerUrl();
      const response = await window.fetch(`${serverUrl}/parse/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials not needed - session token sent via header
        body: JSON.stringify({ sessionToken }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Session login failed');
      }
      const user = await response.json();
      return {
        ...user,
        toJSON: () => user,
        get: (key) => user[key],
        id: user.objectId,
      };
    },
    logInWith: async (provider, { authData }) => {
      // Login via /parse/users with authData (e.g. keycloak)
      const serverUrl = getServerUrl();
      const response = await window.fetch(`${serverUrl}/parse/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials not needed - session token sent via header
        body: JSON.stringify({ authData: { [provider]: authData } }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `${provider} login failed`);
      }
      const user = await response.json();
      return {
        ...user,
        toJSON: () => user,
        get: (key) => user[key],
        id: user.objectId,
      };
    },
    requestPasswordReset: async (email) => {
      const serverUrl = getServerUrl();
      const response = await window.fetch(`${serverUrl}/parse/requestPasswordReset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials not needed - session token sent via header
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Password reset request failed');
      }
    },
  },
  Session: {
    current: async () => {
      // No direct session endpoint; return a minimal object
      const token = getSessionToken();
      if (!token) return null;
      return { getSessionToken: () => token };
    },
  },
  Object: {
    disableSingleInstance: () => {},
    extend: (name) => {
      // Return a constructor-like function for compatibility
      return function(data) {
        this.className = name;
        this.data = data || {};
        this.set = (key, value) => { this.data[key] = value; };
        this.get = (key) => this.data[key];
        this.toJSON = () => ({ ...this.data, className: name });
        this.save = async () => {
          // Use dataExplorer cloud function to save
          const result = await callCloudFunction('dataExplorer_saveData', { dataClass: name, data: this.data });
          return this;
        };
        this.destroy = async () => {
          await callCloudFunction('dataExplorer_deleteData', { dataClass: name, data: this.data });
          return this;
        };
      };
    },
    fromJSON: (json) => {
      return {
        ...json,
        toJSON: () => json,
        get: (key) => json[key],
        set: (key, value) => { json[key] = value; },
        save: async () => json,
      };
    },
  },
  Query: function(dataClass) {
    this.dataClass = dataClass;
    this.constraints = [];
    this.selects_list = null;
    this.equalTo = (key, value) => { this.constraints.push({ key, operator: 'equalTo', value }); return this; };
    this.selects = (fields) => { this.selects_list = fields; return this; };
    this.descending = (key) => { this.constraints.push({ key, operator: 'descending' }); return this; };
    this.find = async () => {
      const result = await callCloudFunction('dataExplorer_searchData', {
        dataClass: this.dataClass,
        params: this.constraints,
      });
      return (result?.results || []).map(r => ({ ...r, toJSON: () => r, get: (k) => r[k] }));
    };
    this.findAll = async () => {
      const result = await callCloudFunction('dataExplorer_searchAllData', {
        dataClass: this.dataClass,
        params: this.constraints,
      });
      return (result?.results || []).map(r => ({ ...r, toJSON: () => r, get: (k) => r[k] }));
    };
    this.subscribe = async () => {
      // LiveQuery is replaced by LiveConnect; return a stub
      return {
        on: () => {},
        unsubscribe: () => {},
      };
    };
  },
  ACL: function() {
    this.acl = {};
    this.setPublicReadAccess = (val) => { this.acl.publicRead = val; };
    this.setRoleWriteAccess = (role, val) => { this.acl[`role_${role}_write`] = val; };
    this.setWriteAccess = (user, val) => { this.acl.userWrite = val; };
  },
  File: function(name, data) {
    this._name = name;
    this._data = data;
    this._url = '';
    this.name = () => this._name;
    this.url = () => this._url;
    this.save = async (options) => {
      const serverUrl = getServerUrl();
      const formData = new FormData();
      const blob = new Blob([this._data]);
      formData.append('file', blob, this._name);

      const response = await window.fetch(`${serverUrl}/parse/files?name=${encodeURIComponent(this._name)}`, {
        method: 'POST',
        // credentials not needed - session token sent via header
        body: blob,
      });

      if (!response.ok) {
        throw new Error('File upload failed');
      }

      const result = await response.json();
      this._name = result.name;
      this._url = result.url;
      return { name: this._name, url: this._url };
    };
  },
  _getInstallationId: async () => {
    return windowIdentitySignal.installationId || 'web';
  },
};

// ============================================================
// ACL helper (kept for backward compatibility)
// ============================================================

export const getACL = () => {
  // Return a simple ACL object - no longer uses Parse.ACL
  return {
    setPublicReadAccess: () => {},
    setRoleWriteAccess: () => {},
    setWriteAccess: () => {},
  };
}

export const excludedFields = [
  "messageKey","objectId","ACL","createdAt","updatedAt","className",
]

const fetchedData = {};

export const getData = (name, id) => {
  const map = fetchedData[name];
  let record = null;
  if (map) {
    record = map[id];
  }
  return record;
}

export const setData = (name, id, record) => {
  let map = fetchedData[name];
  if (!map) {
    map = {};
    fetchedData[name] = map;
  }
  map[id] = record;
}

// ============================================================
// Data access functions - now use cloud functions via HTTP
// ============================================================

export const loadData = async (name, key, id, selects) => {
  try {
    const result = await callCloudFunction('dataExplorer_searchData', {
      dataClass: name,
      params: [{ key, operator: 'equalTo', value: id }],
      selectedColumns: selects,
    });
    const results = result?.results || [];
    if (results.length > 0) {
      if (!fetchedData[name]) fetchedData[name] = {};
      fetchedData[name][id] = results[0];
      return results[0];
    } else {
      return undefined;
    }
  } catch (e) {
    return undefined;
  }
}

export const saveData = async (name, key, id, record, custAcl) => {
  record[key] = id;
  const result = await callCloudFunction('dataExplorer_saveData', {
    dataClass: name,
    data: record,
  });
  return result;
}

export const addData = async (name, key, id, record, custAcl) => {
  const oldRecord = await loadData(name, key, id);
  record[key] = id;
  if (oldRecord) {
    log('record already exists, skipped', record);
    return oldRecord;
  } else {
    const result = await callCloudFunction('dataExplorer_saveData', {
      dataClass: name,
      data: record,
    });
    return result;
  }
}

export const deleteData = async (name, key, id) => {
  const oldRecord = await loadData(name, key, id);
  if (oldRecord) {
    log('deleting record...', oldRecord);
    await callCloudFunction('dataExplorer_deleteData', {
      dataClass: name,
      data: oldRecord,
    });
    return oldRecord;
  } else {
    log('fail to find record for delete.');
  }
}

export const fetch = async (name, key, selects) => {
  try {
    const result = await callCloudFunction('dataExplorer_searchAllData', {
      dataClass: name,
      params: [],
      selectedColumns: selects,
    });
    const results = result?.results || [];
    const resultMap = {};
    for (const element of results) {
      const id = element[key];
      resultMap[id] = element;
    }
    fetchedData[name] = resultMap;
    return resultMap;
  } catch (e) {
    log(`fail to fetch data ${name} ${key}`, e);
    fetchedData[name] = {};
    return {};
  }
}

export const isParseObject = (obj) => {
  // After migration, all objects are plain POJOs
  return obj ? (obj.toJSON !== undefined) : false;
}

export const convertToPOJO = (valueMap) => {
  if (!valueMap) {
    return valueMap;
  } else if (isParseObject(valueMap)) {
    return convertToPOJOSingle(valueMap);
  } else {
    const valueList = Object.values(valueMap);
    return valueList.map((vo => {
      return convertToPOJOSingle(vo);
    }));
  }
}

export const convertToPOJOSingle = (vo) => {
  if (!vo) {
    return vo;
  } else if (vo.toJSON) {
    const json = vo.toJSON();
    return json;
  } else {
    return vo;
  }
}

export const loadDataVersion = async (name, key, id, version) => {
  try {
    const params = [{ key, operator: 'equalTo', value: id }];
    if (version) {
      params.push({ key: 'documentVersion', operator: 'equalTo', value: version });
    } else {
      params.push({ key: 'documentVersion', operator: 'descending' });
    }
    const result = await callCloudFunction('dataExplorer_searchData', {
      dataClass: name,
      params,
    });
    const results = result?.results || [];
    if (results.length > 0) {
      return results[0];
    } else {
      return undefined;
    }
  } catch (e) {
    return undefined;
  }
}

export const saveDataVersion = async (name, key, id, form) => {
  const oldRecord = await loadDataVersion(name, key, id);

  form[key] = id;
  let oldVersion = null;
  if (oldRecord) {
    oldVersion = oldRecord.documentVersion;
    let oldForm = { ...oldRecord };
    oldForm = purifyJson(oldForm, excludedFields);
    oldForm['objectId'] = null;
    if (!oldVersion) {
      oldVersion = 1;
      oldForm["documentVersion"] = oldVersion;
    }

    // Save a copy of the old version
    await callCloudFunction('dataExplorer_saveData', {
      dataClass: name,
      data: { ...oldForm },
    });

    form['objectId'] = oldRecord.objectId;
    form['documentVersion'] = oldVersion + 1;
    const result = await callCloudFunction('dataExplorer_saveData', {
      dataClass: name,
      data: form,
    });
    return result;
  } else {
    const result = await callCloudFunction('dataExplorer_saveData', {
      dataClass: name,
      data: { ...form, documentVersion: 1 },
    });
    return result;
  }
}

export const deleteDataVersion = async (name, key, id, version) => {
  const oldRecord = await loadDataVersion(name, key, id, version);
  if (oldRecord) {
    await callCloudFunction('dataExplorer_deleteData', {
      dataClass: name,
      data: oldRecord,
    });
    return oldRecord;
  } else {
    log('fail to find record for delete.');
  }
}

const getBrowserTimezoneImpl = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

registerService('getBrowserTimezone', getBrowserTimezoneImpl);

const timezone = getBrowserTimezoneImpl();
const RELOAD_QUEUE = new Map();

let isReloadQueueRunning = false;
const fireReloadQueue = async () => {
  if (isReloadQueueRunning === false && RELOAD_QUEUE.size > 0 ) {
    console.log('start reload cache queue...');
    isReloadQueueRunning = true;
    try {
      const batch = [];
      for (const pair of RELOAD_QUEUE) {
        if (batch.length <= 20) {
          batch.push(pair);
        }
      }
      for (const pair of batch) {
        const [key, fn] = pair;
        try {
          await fn();
        } catch (err) {
          console.log('error in reloading cache', err, key);
        }
        RELOAD_QUEUE.delete(key);
      }
      isReloadQueueRunning = false;
      console.log('end reload cache queue');
    } catch (err) {
      console.log('error in running reload cache queue', err);
      isReloadQueueRunning = false;
    }
  }
}

setInterval(async () => {
  fireReloadQueue();
}, 30000);

setTimeout(async () => {
  console.log('start reload all cache');
  await initReloadAllCache();
  console.log('finished reload all cache');
}, 5000)

export const setRequestContext = (context) => {
  let rc = getLocalStorageItemObject('REQUEST_CONTEXT', {});
  if (context) {
    rc = {...rc, ...context}
  }
  setLocalStorageItemObject('REQUEST_CONTEXT', rc);
}

export const getRequestContext = () => {
  const userLocale = getUserLocale();
  const accessToken = keycloakApi.keycloak?.token;
  const windowIdentity = windowIdentitySignal.value;
  const installationId = windowIdentitySignal.installationId;
  const rcText = getLocalStorageItem('REQUEST_CONTEXT');
  let rc = tryParseJson(rcText) || {}
  return {...rc, timezone, userLocale, accessToken, windowIdentity, installationId}
}

const RequestContextListener = {};

export const registerRequestContextListener = (key, listener) => {
  RequestContextListener[key] = ({key, listener})
}

export const unregisterRequestContextListener = (key, listener) => {
  RequestContextListener[key] = undefined;
}

const fireRequestContextChange = (event) => {
  const oldValue = tryParseJson(event.oldValue) || {};
  const newValue = tryParseJson(event.newValue) || {};
  Object.values(RequestContextListener).forEach((rcl) => {
    if (rcl?.listener) {
      try {
        rcl.listener({ oldValue, newValue });
      } catch (error) {
        console.log("failed to execute request context listener", rcl, error);
      }
    }
  });
}

console.log('register storage listener...');
window.addEventListener('storage', function(event){
  console.log('storage event', event.key, event)
  if (event.key === getLocalStoragePrefix() + 'REQUEST_CONTEXT') {
    fireRequestContextChange(event);
  }
});

export const addCtx = (params) => {
  const requestContext = getRequestContext()
  const newParams = {...params, requestContext};
  if (AccountStore.COMPRESS_REQUEST_PARAMS) {
    return {
      isCompressParams: true,
      compressedParams: compressJsonData(JSON.stringify({...newParams})),
    }
  } else {
    return newParams;
  }
}

export const CloudRunWithoutCache = async (f, param) => {
  await parseServerInitializedSignal.waitFor('value', true);
  return callCloudFunction(f, addCtx(param));
};

export const prepareIdbCacheKey = (f, param) => {
  const {locale, ...newParam} = param || {};
  const key = f+JSON.stringify(newParam);
  return key;
}

let isReloading = false;
let isInitialized = false;
const initReloadAllCache = async () => {
  if (!isInitialized) {
    isInitialized = true;
    await reloadAllCache();
  }
}

const getReloadDataDecision = async () => {
  const lastModified = await system.getActiveDataLastModified();
  const sharedLastModified = await system.getSharedDataLastModified();
  const oldLastModified = getLocalStorageItem("ActiveDataLastModified")
  const oldSharedLastModified = getLocalStorageItemObject("SharedDataLastModified")
  const noReloadActiveData = lastModified === oldLastModified;
  const noReloadSharedData = equals(sharedLastModified, oldSharedLastModified);
  const decision = {
    lastModified,
    sharedLastModified,
    oldLastModified,
    oldSharedLastModified,
    noReloadActiveData,
    noReloadSharedData};
  return decision;
}

const reloadAllCache = async () => {
  try {
    if (isReloading) {
      await waitForCondition(() => !isReloading, {label: 'reloadAllCache'})
    } else {
      const decision = await getReloadDataDecision();
      log("getReloadDataDecision()", {decision});
      const keyMap = await idb_get(localLastUpdateDateMap) || {};
      isReloading = true;
      const keys = await idb_allKeys('cache');
      if (keys) {
        const sortedKeys = sortKeysByLastUpdateDate(keyMap, keys);
        for (const key of sortedKeys) {
          if(isCacheNeedReload({key, ...decision})) {
            idb_del(key);
          }
        }
        for (const key of sortedKeys) {
          const lastUpdate = keyMap[key];
          if (!lastUpdate || isDateLongerThanMinutes(lastUpdate, 10)) {
            await reloadAllCacheOne({key, ...decision})
            keyMap[key] = new Date();
            idb_set(localLastUpdateDateMap, keyMap);
          }
        }
        if (!decision.noReloadActiveData) setLocalStorageItem("ActiveDataLastModified", `${decision.lastModified}`);
        if (!decision.noReloadSharedData) setLocalStorageItemObject("SharedDataLastModified", decision.sharedLastModified);
      }
    }
    isReloading = false;
  } catch (e2) {
    isReloading = false;
    console.log('reloadAllCache() error', e2)
  }
}

const getLocalDataUpdatedAt = (old) => {
  let updatedAt = null;
  if (old) {
    if (Array.isArray(old)) {
      for (const o in old) {
        if (!updatedAt || o?.updatedAt > updatedAt) {
          updatedAt = o?.updatedAt;
        }
      }
    } else {
      updatedAt = old.updateAt;
    }
  }
  return updatedAt;
}

const isActiveDataNeedReload = async ({
  key, needReload, noReloadActiveData, lastModified
}) => {
  if (isActiveDataKey(key)) {
    if (noReloadActiveData) {
      needReload = false;
    } else {
      const old = await idb_get(key)
      const updatedAt = getLocalDataUpdatedAt(old);
      if (updatedAt && updatedAt > lastModified) {
        needReload = false;
      }
    }
  }
  return needReload;
}

const isSharedDataNeedReload = async ({
  key, needReload, noReloadActiveData, noReloadSharedData, lastModified, sharedLastModified
}) => {
  if (isSharedDataKey(key)) {
    if (noReloadActiveData && noReloadSharedData) {
      needReload = false;
    } else {
      const old = await idb_get(key);
      const updatedAt = getLocalDataUpdatedAt(old);
      if (updatedAt && updatedAt > lastModified && updatedAt > sharedLastModified) {
        needReload = false;
      }
    }
  }
  return needReload;
}

const isCacheNeedReload = ({
  key, lastModified, sharedLastModified,
  noReloadActiveData, noReloadSharedData}) => {
  const params = {key, noReloadActiveData,
    noReloadSharedData, lastModified, sharedLastModified}
  if (!key.includes('"isPreview":true')) {
    params.needReload = true;
    if (params.needReload && !isActiveDataNeedReload(params)) params.needReload = false;
    if (params.needReload && !isSharedDataNeedReload(params)) params.needReload = false;
    return params.needReload;
  } else {
    idb_del(key);
    return false;
  }
}

const reloadAllCacheOne = async ({
  key, lastModified, sharedLastModified,
  noReloadActiveData, noReloadSharedData}) => {
  const params = {key, noReloadActiveData,
    noReloadSharedData, lastModified, sharedLastModified}
  try {
    if (!key.includes('"isPreview":true')) {
      params.needReload = true;
      if (params.needReload && !isActiveDataNeedReload(params)) params.needReload = false;
      if (params.needReload && !isSharedDataNeedReload(params)) params.needReload = false;
      if (params.needReload) {
        await reloadAllCacheOneImpl({key})
      }
    } else {
      idb_del(key);
    }
  } catch (e1) {
    if (`${e1}`.includes('NOTFOUND')) {
      idb_del(key);
    } else {
      console.log('reloadAllCache() error', key, e1)
    }
  }
}

const reloadAllCacheOneImpl = async ({key}) => {
  const start = key.indexOf('{');
  const fn = key.substring(0, start);
  const pm = key.substring(start);
  const param = JSON.parse(pm);
  const cachedData = await idb_get(key);
  if (cachedData?.versionStamp) {
    param.currentVersionStamp = cachedData.versionStamp;
  }
  const result = await CloudRunWithCacheImpl(fn, param, true);
  if (result && result !== "NO_UPDATE") {
    idb_set(key, result);
  }
}

const performPageReloadCondition = async ({key, cachedData, result}) => {
  if (isReloading) return;
  const reloadCacheKey = 'reload_cache';
  if (cachedData?.versionStamp && result?.versionStamp) {
    if (isReloadPageKey(key)) {
      if (cachedData?.versionStamp !== result?.versionStamp) {
        console.log("local cache updated trigger", {cachedData, result})
        await reloadAllCache();
        message.info({
          content: getLbl(
            "system.new_version_cache_available.description",
            "A new version is loaded into cache."
          ),
          key: reloadCacheKey,
        });
      }
    }
  }
}

const relelaseLock = (lock, releaseKey, data, error) => {
  if (releaseKey) {
    lock.setData(releaseKey, data, error);
    delay(10000).then(() => {
      lock.release(releaseKey, data, error);
    })
  }
}

export const CloudRunWithCache = async (f, param, nocache, defaultValue) => {
  const key = prepareIdbCacheKey(f, param);
  let cachedData = null;
  let releaseKey = null;
  const lock = DataRetrievalLock.getLock(key);

  const data = await lock.acquire(30000, nocache);
  if (data) {
    releaseKey = DataRetrievalLock.getReleaseKey(data);
    if (releaseKey) {
      console.log('CloudRunWithCache releaseKey', releaseKey, key)
    }
    if (!releaseKey) {
      return data;
    }
  }

  if (!nocache) {
    cachedData = await idb_get(key);
  }

  await waitForCondition(() => !isReloading, {timeout: 100, label: 'CloudRunWithCache'})

  if (cachedData) {
    if (isReloadDataKey(key) || isReloadPageKey(key) || isSelectKey(key)) {
      RELOAD_QUEUE.set(key, async () => {
        try {
          const result = await CloudRunWithCacheImpl(f, param, defaultValue);
          idb_set(key, result);
          performPageReloadCondition({key, cachedData, result});
        } catch (error) {
          console.log("error", error);
        }
      })
      fireReloadQueue();
    }
    relelaseLock(lock, releaseKey, cachedData);
    return cachedData;
  } else {
    let result = null;
    try {
      result = await CloudRunWithCacheImpl(f, param, defaultValue);
      idb_set(key, result);
      relelaseLock(lock, releaseKey, result);
    } catch (error) {
      console.log("error", error);
      relelaseLock(lock, releaseKey, null, error);
      throw error;
    }
    return result;
  }
}

export const CloudRunWithCacheImpl = async (f, param, defaultValue) => {
  let serverOffline = getLocalStorageItem('server_offline');
  if (serverOffline) {
    const current = new Date().getMilliseconds();
    if (current - serverOffline > (1000 * 60)) {
      removeLocalStorageItem('server_offline');
      serverOffline = null;
    }
  }
  if (serverOffline) {
    return defaultValue;
  }
  try {
    await parseServerInitializedSignal.waitFor('value', true);
    const data = await callCloudFunction(f, addCtx(param));
    return data;
  } catch (error) {
    if (updateServerOfflineStatus(error)) {
      // is offline
      return defaultValue;
    } else {
      throw error;
    }
  }
}

export const getServerOffline = async () => {
  try {
    const param = {};
    await CloudRunWithoutCache("getSystemVersion", param);
    updateServerOfflineStatus(null);
    return false;
  } catch (error) {
    return !!(updateServerOfflineStatus(error));
  }
};

const updateServerOfflineStatus = (error) => {
  if (error) {
    if (!`${error}`.match(/(Unable to connect to the Parse API|has been blocked by CORS policy|Failed to fetch|NetworkError)/)) {
      return false;
    } else {
      setLocalStorageItem('server_offline', new Date().getTime());
      return true;
    }
  } else {
    removeLocalStorageItem('server_offline');
    return false;
  }
}

initializeParseSDK();

export default parse;
